// ── Enums ──────────────────────────────────────────────

export type UserRole = 'ADMIN' | 'MANAGER' | 'OFFICER' | 'COLLECTOR' | 'VIEWER';
export type LoansAppRole = 'loans_admin' | 'loans_user';
export type AppRole =
  | 'sr_admin'
  | 'sr_issuer_admin'
  | 'sr_issuer'
  | 'sr_investor'
  | 'compliance_admin'
  | 'compliance_user'
  | 'loans_admin'
  | 'loans_user'
  | 'academy_user'
  | 'academy_organisation_admin';

export interface AvailableTenant {
  id: string;
  name: string;
  organizationId?: string | null;
  appRole?: LoansAppRole | null;
}

export type LoanStatus =
  | 'APPLICATION'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'DISBURSED'
  | 'CURRENT'
  | 'DELINQUENT_30'
  | 'DELINQUENT_60'
  | 'DELINQUENT_90'
  | 'DEFAULT'
  | 'PAID_OFF'
  | 'RESTRUCTURED'
  | 'CANCELLED';

export type LoanType = 'PERSONAL' | 'BUSINESS' | 'MORTGAGE' | 'AUTO' | 'LINE_OF_CREDIT';

export type AssignmentRole = 'OWNER' | 'REVIEWER' | 'COLLECTOR' | 'WATCHER';

export type ScheduleItemStatus = 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE' | 'WAIVED';

export type SecurityType =
  | 'REAL_PROPERTY'
  | 'MOTOR_VEHICLE'
  | 'EQUIPMENT'
  | 'STOCK_INVENTORY'
  | 'ACCOUNTS_RECEIVABLE'
  | 'PERSONAL_GUARANTEE'
  | 'CASH_DEPOSIT'
  | 'SHARES_SECURITIES'
  | 'OTHER';

// ── Models ─────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  appRole?: LoansAppRole;
  organizationId?: string;
  keycloakUserId?: string;
  availableAppRoles?: AppRole[];
  tenantId?: string;
  allowedTenantIds?: string[];
  availableTenants?: AvailableTenant[];
  isActive?: boolean;
  ssoProvider?: string;
  ssoId?: string;
  avatarUrl?: string;
  displayName?: string;
  jobTitle?: string;
  department?: string;
  phoneNumber?: string;
  lastLoginAt?: string;
  loginCount?: number;
  /** anthropic | none (none = AI features off for this user) */
  aiLlmProvider?: string;
  /** Optional Anthropic model id; unset = platform default */
  aiLlmModel?: string | null;
  createdAt?: string;
  updatedAt?: string;
  meta?: any;
}

export type BorrowerEntityType =
  | 'INDIVIDUAL'
  | 'SOLE_TRADER'
  | 'PARTNERSHIP'
  | 'COMPANY'
  | 'TRUST'
  | 'SMSF'
  | 'ASSOCIATION'
  | 'GOVERNMENT'
  | 'JOINT_VENTURE'
  | 'OTHER';

export const BORROWER_ENTITY_TYPE_LABELS: Record<BorrowerEntityType, string> = {
  INDIVIDUAL: 'Individual',
  SOLE_TRADER: 'Sole Trader',
  PARTNERSHIP: 'Partnership',
  COMPANY: 'Company (Pty Ltd)',
  TRUST: 'Trust',
  SMSF: 'Self-Managed Super Fund',
  ASSOCIATION: 'Association',
  GOVERNMENT: 'Government Entity',
  JOINT_VENTURE: 'Joint Venture',
  OTHER: 'Other',
};

export interface AbnLookupResult {
  abn: string;
  abnStatus: string;
  entityType: string;
  entityTypeDescription: string;
  entityName: string;
  tradingName?: string;
  gstRegistered: boolean;
  gstRegisteredFrom?: string;
  state?: string;
  postcode?: string;
  acn?: string;
}

export interface AbnSearchResult {
  abn: string;
  name: string;
  nameType: string;
  state: string;
  postcode: string;
  score: number;
}

export interface Director {
  name: string;
  role: string;
  dateAppointed?: string;
}

export interface Borrower {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  taxId?: string;
  address?: Record<string, string>;
  createdAt?: string;
  // Australian entity extensions
  entityType?: BorrowerEntityType;
  abn?: string;
  acn?: string;
  abnStatus?: string;
  abnVerifiedAt?: string;
  entityName?: string;
  gstRegistered?: boolean;
  trustType?: string;
  trusteeType?: string;
  trusteeName?: string;
  directors?: Director[];
}

export interface Loan {
  id: string;
  loanNumber: string;
  borrowerId: string;
  ledgerId: string;
  loanType: LoanType;
  status: LoanStatus;

  // NOTE: All Decimal fields below come as STRING from the API due to precision requirements.
  // When using these values for calculations or comparisons, convert to number with parseFloat()
  // or use a Decimal library. Do NOT rely on implicit type coercion.
  principal: string; // Decimal: Principal amount (from Prisma Decimal type)
  interestRate: string; // Decimal: Annual interest rate percentage (from Prisma Decimal type)
  interestMethod?: 'PERCENTAGE' | 'FLAT_AMOUNT';
  flatInterestAmount?: string; // Decimal: Total flat interest for the loan term
  paymentFrequency?: string;
  termMonths: number;
  applicationDate: string;
  approvalDate?: string;
  settlementDate?: string;
  disbursementDate?: string;
  maturityDate?: string;
  closedDate?: string;
  defaultDate?: string;
  createdAt: string;
  updatedAt: string;

  // Dual interest rates
  defaultRate?: string; // Decimal: Higher rate applied on default (from Prisma Decimal type)

  // Establishment costs & fees
  establishmentFee?: string; // Decimal: (from Prisma Decimal type)
  brokerageFee?: string; // Decimal: (from Prisma Decimal type)
  legalCosts?: string; // Decimal: (from Prisma Decimal type)
  legalCostsRecovered?: boolean;
  otherCosts?: string; // Decimal: (from Prisma Decimal type)
  otherCostsDescription?: string;

  // Fee split - lender retained portion
  establishmentFeeRetained?: string; // Decimal: (from Prisma Decimal type)
  brokerageFeeRetained?: string; // Decimal: (from Prisma Decimal type)
  referrerCommission?: string; // Decimal: (from Prisma Decimal type)

  // Loan references
  internalReference?: string;
  lawFirmReference?: string;
  externalReference?: string; // Editable Loan ID shown on documents (e.g. Summers trust account number)
  legalMatterRef?: string;

  // Referrer
  referrerId?: string;

  // Populated relations
  borrower?: Borrower;
  referrer?: { id: string; name: string; company?: string; commissionRate?: string };
  assignments?: LoanAssignment[];
  schedule?: RepaymentScheduleItem[];
  ledger?: Ledger;
  securities?: SecurityItem[];
  extensions?: LoanExtension[];
  borrowerNotification?: {
    status: 'SENT' | 'SKIPPED' | 'FAILED';
    reason?:
      | 'UNSUPPORTED_TRANSITION'
      | 'MISSING_BORROWER_EMAIL'
      | 'EMAIL_TRANSPORT_NOT_CONFIGURED'
      | 'DELIVERY_FAILED';
  };
}

export interface LoanExtension {
  id: string;
  loanId: string;
  extensionNumber: number;
  previousTermMonths: number;
  newTermMonths: number;
  additionalMonths: number;
  previousMaturityDate?: string;
  newMaturityDate?: string;
  extensionFee: string;
  extensionFeeRetained: string;
  extensionFeePaidOut: string;
  previousInterestRate?: string;
  newInterestRate?: string;
  reason?: string;
  notes?: string;
  approvedById?: string;
  approvedBy?: { id: string; firstName: string; lastName: string; role?: string };
  createdAt: string;
}

export interface Ledger {
  id: string;
  name: string;
  balances?: Balance[];
}

export interface Balance {
  id: string;
  ledgerId: string;
  indicator: string;
  balance: string;
  creditBalance: string;
  debitBalance: string;
  currency: string;
}

export interface Transaction {
  id: string;
  sourceBalanceId: string;
  destinationBalanceId: string;
  amount: string;
  currency: string;
  reference?: string;
  description?: string;
  status: 'QUEUED' | 'APPLIED' | 'VOID';
  createdAt: string;
  sourceBalance?: { indicator: string };
  destinationBalance?: { indicator: string };
}

export interface RepaymentScheduleItem {
  id: string;
  loanId: string;
  dueDate: string;
  periodNumber: number;
  principalDue: string;
  interestDue: string;
  feesDue: string;
  totalDue: string;
  status: ScheduleItemStatus;
  paidDate?: string;
  paidAmount?: string;
}

export interface LoanAssignment {
  id: string;
  loanId: string;
  userId: string;
  role: AssignmentRole;
  assignedAt: string;
  user?: User;
}

export interface Comment {
  id: string;
  loanId: string;
  authorId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  author?: User;
}

export interface ActivityLogEntry {
  id: string;
  loanId: string;
  actorId?: string;
  actionType: string;
  details?: Record<string, any>;
  createdAt: string;
  actor?: User;
  loan?: {
    id: string;
    loanNumber: string;
    status: LoanStatus;
    borrower?: { id: string; name: string };
  };
}

export interface SecurityItem {
  id: string;
  loanId: string;
  description: string;
  securityType: SecurityType;
  priority: number;
  priorEncumbrances: number;
  existingDebt?: string;
  existingDebtHolder?: string;
  borrowerValuation?: string;
  internalEstimate?: string;
  valuerEstimate?: string;
  valuationDate?: string;
  valuerName?: string;
  status: string;
  notes?: string;
  // Structured address fields
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SecuritySummary {
  itemCount: number;
  totalBorrowerValuation: number;
  totalInternalEstimate: number;
  totalValuerEstimate: number;
  totalExistingDebt: number;
  bestEstimate: number;
  netEquity: number;
  loanAmount: number;
  // LTV metrics
  seniorLtv: number;
  totalLtv: number;
  ourLtv: number;
  securityCover: number;
  // Risk stack
  riskStack: {
    seniorDebt: number;
    ourTranche: number;
    availableEquity: number;
    totalValue: number;
  };
  items: (SecurityItem & {
    bestValuation: number;
    availableEquity: number;
  })[];
}

export const SECURITY_TYPE_LABELS: Record<SecurityType, string> = {
  REAL_PROPERTY: 'Real Property',
  MOTOR_VEHICLE: 'Motor Vehicle',
  EQUIPMENT: 'Equipment',
  STOCK_INVENTORY: 'Stock / Inventory',
  ACCOUNTS_RECEIVABLE: 'Accounts Receivable',
  PERSONAL_GUARANTEE: 'Personal Guarantee',
  CASH_DEPOSIT: 'Cash Deposit',
  SHARES_SECURITIES: 'Shares / Securities',
  OTHER: 'Other',
};

// ── API Responses ──────────────────────────────────────

export interface PaginatedResponse<T> {
  loans: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuthResponse {
  user: User;
  token: string;
}

// ── Status Display Config ──────────────────────────────

export const LOAN_STATUS_CONFIG: Record<
  LoanStatus,
  { label: string; color: string; bgColor: string }
> = {
  APPLICATION: { label: 'Application', color: '#6366f1', bgColor: '#eef2ff' },
  UNDER_REVIEW: { label: 'Under Review', color: '#f59e0b', bgColor: '#fffbeb' },
  APPROVED: { label: 'Approved', color: '#10b981', bgColor: '#ecfdf5' },
  DISBURSED: { label: 'Disbursed', color: '#3b82f6', bgColor: '#eff6ff' },
  CURRENT: { label: 'Current', color: '#22c55e', bgColor: '#f0fdf4' },
  DELINQUENT_30: { label: '30+ Days', color: '#ef4444', bgColor: '#fef2f2' },
  DELINQUENT_60: { label: '60+ Days', color: '#dc2626', bgColor: '#fef2f2' },
  DELINQUENT_90: { label: '90+ Days', color: '#b91c1c', bgColor: '#fef2f2' },
  DEFAULT: { label: 'Default', color: '#991b1b', bgColor: '#fef2f2' },
  PAID_OFF: { label: 'Paid Off', color: '#6b7280', bgColor: '#f9fafb' },
  RESTRUCTURED: { label: 'Restructured', color: '#8b5cf6', bgColor: '#f5f3ff' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bgColor: '#f9fafb' },
};

// ── Notification types ────────────────────────────────

export type NotificationType =
  | 'STATUS_CHANGE'
  | 'COMMENT'
  | 'ASSIGNMENT'
  | 'PAYMENT_DUE'
  | 'OVERDUE'
  | 'SYSTEM'
  | 'DOCUMENT'
  | 'APPROVAL';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  relatedLoanId?: string;
  relatedCommentId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationGroup {
  type: NotificationType;
  count: number;
  notifications: Notification[];
}

// ── Search & Filter types ─────────────────────────────

export interface SavedSearch {
  id: string;
  name: string;
  filters: SearchFilters;
  createdAt: string;
  userId: string;
}

export interface SearchFilters {
  text?: string;
  status?: LoanStatus[];
  loanType?: LoanType[];
  fundId?: string;
  referrerId?: string;
  applicationDateRange?: { start?: string; end?: string };
  maturityDateRange?: { start?: string; end?: string };
  principalRange?: { min?: number; max?: number };
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface SearchResult {
  loans: Loan[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ── Report Builder types ──────────────────────────────

export type ReportDimension =
  'status' | 'loanType' | 'fund' | 'referrer' | 'month' | 'quarter' | 'year' | 'state';

export type ReportMeasure =
  'count' | 'totalPrincipal' | 'avgPrincipal' | 'totalFees' | 'avgInterestRate' | 'avgLTV';

export type ReportChartType = 'bar' | 'line' | 'pie' | 'table';

export interface ReportConfig {
  id?: string;
  name?: string;
  dimensions: ReportDimension[];
  measures: ReportMeasure[];
  chartType: ReportChartType;
  filters?: SearchFilters;
  createdAt?: string;
  userId?: string;
}

export interface ReportData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string[];
  }[];
  rows?: Record<string, any>[];
}

// ── Tranche types ────────────────────────────────────

export type TrancheStatus = 'PLANNED' | 'SCHEDULED' | 'APPROVED' | 'DRAWN' | 'CANCELLED';

export interface Tranche {
  id: string;
  loanId: string;
  number: number;
  amount: string;
  purpose?: string;
  status: TrancheStatus;
  scheduledDate?: string;
  drawnDate?: string;
  conditions?: TrancheCondition[];
  createdAt: string;
  updatedAt: string;
}

export interface TrancheCondition {
  id: string;
  trancheId: string;
  description: string;
  completed: boolean;
  completedDate?: string;
  note?: string;
}

export interface TrancheDrawdownSummary {
  facilityLimit?: number;
  totalCommitted: number;
  totalDrawn: number;
  remaining: number;
  availableToDraw?: number;
  percentageUtilised: number;
  tranches: Tranche[];
}

// ── Interest Accrual types ───────────────────────────

export interface InterestAccrual {
  id: string;
  loanId: string;
  accrualDate: string;
  amount: string;
  principalBalance: string;
  rate: string;
  dayCountMethod?: string;
  description?: string;
  createdAt: string;
}

export interface InterestCapitalisation {
  id: string;
  loanId: string;
  capitalisationDate: string;
  amount: string;
  previousPrincipal: string;
  newPrincipal: string;
  createdAt: string;
}

export interface BrokenPeriodCalculation {
  startDate: string;
  endDate: string;
  daysCount: number;
  rate: number;
  principal: number;
  amount: number;
  dayCountMethod: string;
}

// ── Document Template types ──────────────────────────

export interface DocumentTemplate {
  id: string;
  name: string;
  category: string;
  subject?: string;
  body: string;
  mergeFields?: string[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
}

export interface MergeFieldGroup {
  entity: string;
  fields: {
    name: string;
    label: string;
    type?: string;
  }[];
}

export interface RenderedDocument {
  html: string;
  plaintext: string;
  mergeFieldsUsed: string[];
}

// ── TOTP/2FA types ────────────────────────────────────

export interface TOTPSetupResponse {
  secret: string;
  uri: string;
  qrCodeDataUrl?: string;
}

export interface TOTPStatus {
  enabled: boolean;
  backupCodesCount: number;
  lastBackupCodesGenerated?: string;
}

export interface BackupCodesResponse {
  codes: string[];
  generatedAt: string;
}

// ── Guarantor Types ────────────────────────────────────

export interface Guarantor {
  id: string;
  loanId: string;
  name: string;
  entityType: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
  abn?: string;
  email?: string;
  phone?: string;
  guaranteeType: 'PERSONAL' | 'CORPORATE' | 'DIRECTOR' | 'THIRD_PARTY' | 'LIMITED_RECOURSE';
  limitOfLiability: string;
  guaranteeDate?: string;
  expiryDate?: string;
  status: 'ACTIVE' | 'RELEASED' | 'EXPIRED';
  notes?: string;
  createdAt: string;
  releasedAt?: string;
  releasedReason?: string;
}

export interface GuarantorSummary {
  totalGuarantors: number;
  totalLiability: string;
  activeCount: number;
  releasedCount: number;
  expiredCount: number;
}

// ── Condition Types ────────────────────────────────────

export interface LoanCondition {
  id: string;
  loanId: string;
  description: string;
  category:
    | 'DOCUMENTATION'
    | 'COMPLIANCE'
    | 'SECURITY'
    | 'INSURANCE'
    | 'VALUATION'
    | 'LEGAL'
    | 'FINANCIAL'
    | 'OTHER';
  type: 'PRECEDENT' | 'SUBSEQUENT';
  status: 'OUTSTANDING' | 'SATISFIED' | 'WAIVED' | 'OVERDUE' | 'NOT_APPLICABLE';
  dueDate?: string;
  satisfiedDate?: string;
  waivedDate?: string;
  waiverReason?: string;
  documentReference?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConditionTemplate {
  id: string;
  description: string;
  category: LoanCondition['category'];
  type: 'PRECEDENT' | 'SUBSEQUENT';
  commonDueDate?: number;
}

// ── Collection Types ────────────────────────────────────

export type CollectionStage =
  | 'FRIENDLY_REMINDER'
  | 'FORMAL_DEMAND'
  | 'DEFAULT_NOTICE'
  | 'LEGAL_REFERRAL'
  | 'ENFORCEMENT'
  | 'RESOLVED';

export interface CollectionCase {
  id: string;
  loanId: string;
  loanNumber: string;
  borrowerId: string;
  borrowerName: string;
  stage: CollectionStage;
  daysPastDue: number;
  arrearsAmount: string;
  assignedToId?: string;
  assignedToName?: string;
  createdAt: string;
  updatedAt: string;
  resolutionType?: 'PAYMENT_RECEIVED' | 'RESTRUCTURED' | 'WRITE_OFF' | 'WITHDRAWN' | 'LEGAL_ACTION';
  resolutionDate?: string;
  notes?: string;
}

export interface CollectionDashboard {
  byStage: Record<CollectionStage, { count: number; totalAmount: string }>;
  totalCases: number;
  totalArrears: string;
}

// ── Pipeline Types ────────────────────────────────────

export type PipelineStage =
  | 'ENQUIRY'
  | 'INDICATIVE_TERMS'
  | 'FORMAL_APPLICATION'
  | 'CREDIT_ASSESSMENT'
  | 'APPROVAL'
  | 'DOCUMENTATION'
  | 'SETTLEMENT'
  | 'FUNDED'
  | 'DECLINED'
  | 'WITHDRAWN';

export interface PipelineEntry {
  id: string;
  borrowerName: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  referrerId?: string;
  referrerName?: string;
  requestedAmount: string;
  stage: PipelineStage;
  probability: number;
  expectedSettlementDate?: string;
  assignedToId?: string;
  assignedToName?: string;
  loanPurpose?: string;
  propertyType?: string;
  securityAddress?: string;
  estimatedLVR?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  loanId?: string;
}

export interface PipelineStats {
  totalPipelineValue: string;
  entriesByStage: Record<PipelineStage, number>;
  winRate: number;
  avgDaysToSettlement: number;
  totalEntries: number;
}

// ── Benchmark Types ────────────────────────────────────

export interface BenchmarkRate {
  name: string;
  currentRate: number;
  lastUpdatedDate: string;
  description?: string;
}

export interface MarginAnalysis {
  loanId: string;
  loanNumber: string;
  currentRate: number;
  baseRate: number;
  margin: number;
  targetMargin: number;
  status: 'ABOVE_TARGET' | 'AT_TARGET' | 'COMPRESSED' | 'CRITICAL';
  borrowerName: string;
}

export interface CompressedMarginAlert {
  count: number;
  totalAmount: string;
  loans: MarginAnalysis[];
}

// ── Audit Dashboard Types ────────────────────────────────

export interface SystemHealth {
  activeUsers: number;
  failedLogins24h: number;
  activeSessions: number;
  pendingCompliance: number;
  uptime: number;
  lastHealthCheck: string;
}

export interface ActiveSession {
  id: string;
  userId: string;
  userName: string;
  ipAddress: string;
  userAgent: string;
  lastActivity: string;
  createdAt: string;
}

export interface PermissionChange {
  id: string;
  userId: string;
  userName: string;
  changedById: string;
  changedByName: string;
  changeType: 'GRANTED' | 'REVOKED' | 'MODIFIED';
  previousPermissions?: string;
  newPermissions?: string;
  reason?: string;
  date: string;
}

export interface RetentionPolicy {
  id: string;
  entityType: string;
  retentionDays: number;
  lastRunDate?: string;
  status: 'ACTIVE' | 'PAUSED' | 'ERROR';
  description?: string;
}

export interface Soc2Summary {
  categories: {
    name: 'Encryption' | 'Access Control' | 'Audit Trail' | 'Data Retention' | 'Compliance';
    score: number;
    status: 'PASS' | 'CAUTION' | 'FAIL';
    recommendations: string[];
  }[];
  overallScore: number;
  lastAssessment: string;
}

// ── Restructure Types ────────────────────────────────────

export type RestructureType =
  | 'RATE_CHANGE'
  | 'TERM_EXTENSION'
  | 'LIMIT_INCREASE'
  | 'LIMIT_DECREASE'
  | 'HARDSHIP_VARIATION'
  | 'FULL_RESTRUCTURE'
  | 'PAYMENT_HOLIDAY';

export type RestructureStatus =
  'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'APPLIED' | 'CANCELLED';

export const RESTRUCTURE_TYPE_LABELS: Record<RestructureType, string> = {
  RATE_CHANGE: 'Rate Change',
  TERM_EXTENSION: 'Term Extension',
  LIMIT_INCREASE: 'Limit Increase',
  LIMIT_DECREASE: 'Limit Decrease',
  HARDSHIP_VARIATION: 'Hardship Variation',
  FULL_RESTRUCTURE: 'Full Restructure',
  PAYMENT_HOLIDAY: 'Payment Holiday',
};

export const RESTRUCTURE_STATUS_CONFIG: Record<
  RestructureStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  PENDING_APPROVAL: { label: 'Pending Approval', color: '#f59e0b', bgColor: '#fffbeb' },
  APPROVED: { label: 'Approved', color: '#10b981', bgColor: '#ecfdf5' },
  REJECTED: { label: 'Rejected', color: '#ef4444', bgColor: '#fef2f2' },
  APPLIED: { label: 'Applied', color: '#6366f1', bgColor: '#eef2ff' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bgColor: '#f9fafb' },
};

export interface LoanRestructure {
  id: string;
  loanId: string;
  restructureNumber: number;
  type: RestructureType;
  status: RestructureStatus;
  originalPrincipal: string;
  originalRate: string;
  originalTermMonths: number;
  originalMaturityDate?: string;
  proposedPrincipal?: string;
  proposedRate?: string;
  proposedTermMonths?: number;
  proposedMaturityDate?: string;
  proposedPaymentFrequency?: string;
  hardshipCategory?: string;
  hardshipDetails?: string;
  reason?: string;
  internalNotes?: string;
  requestedById: string;
  requestedBy?: { id: string; firstName: string; lastName: string };
  approvedById?: string;
  approvedBy?: { id: string; firstName: string; lastName: string };
  approvedAt?: string;
  effectiveDate?: string;
  variationLetter?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Commission Types ────────────────────────────────────

export type CommissionType = 'UPFRONT' | 'TRAIL' | 'CLAWBACK';
export type CommissionPaymentStatus =
  'CALCULATED' | 'APPROVED' | 'PAID' | 'CLAWED_BACK' | 'CANCELLED';

export interface CommissionStructure {
  id: string;
  referrerId: string;
  name: string;
  upfrontRate: string;
  trailRate: string;
  clawbackMonths: number;
  minLoanAmount?: string;
  maxLoanAmount?: string;
  isDefault: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface CommissionPayment {
  id: string;
  referrerId: string;
  loanId: string;
  type: CommissionType;
  amount: string;
  gstAmount: string;
  totalAmount: string;
  rate: string;
  principalBase: string;
  status: CommissionPaymentStatus;
  periodStart?: string;
  periodEnd?: string;
  paidDate?: string;
  paymentReference?: string;
  clawbackEligible: boolean;
  clawbackDate?: string;
  clawedBack: boolean;
  loanNumber?: string;
  borrowerName?: string;
  createdAt: string;
}

export interface CommissionDashboard {
  totalEarned: string;
  totalPending: string;
  totalPaid: string;
  clawbackRisk: string;
  upfrontTotal: string;
  trailTotal: string;
  paymentCount: number;
}

// ── Risk Scoring Types ────────────────────────────────────

export type RiskRating = 'LOW' | 'MODERATE' | 'ELEVATED' | 'HIGH' | 'CRITICAL';

export const RISK_RATING_CONFIG: Record<
  RiskRating,
  { label: string; color: string; bgColor: string }
> = {
  LOW: { label: 'Low Risk', color: '#22c55e', bgColor: '#f0fdf4' },
  MODERATE: { label: 'Moderate', color: '#eab308', bgColor: '#fefce8' },
  ELEVATED: { label: 'Elevated', color: '#f97316', bgColor: '#fff7ed' },
  HIGH: { label: 'High Risk', color: '#ef4444', bgColor: '#fef2f2' },
  CRITICAL: { label: 'Critical', color: '#991b1b', bgColor: '#fef2f2' },
};

export interface RiskScore {
  id: string;
  loanId: string;
  overallScore: number;
  riskRating: RiskRating;
  factors: { factor: string; score: number; weight: number; detail: string }[];
  recommendation?: string;
  scoredBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface RiskScorecard {
  id: string;
  name: string;
  version: number;
  loanTypes: string[];
  factors: {
    name: string;
    weight: number;
    thresholds: { min: number; max: number; score: number }[];
  }[];
  isActive: boolean;
}

export interface PortfolioRiskMap {
  byRating: Record<RiskRating, { count: number; totalExposure: number }>;
  avgScore: number;
  totalLoansScored: number;
  riskWeightedExposure: number;
}

export interface ConcentrationRisk {
  borrowerConcentration: { borrowerName: string; exposure: number; percentage: number }[];
  sectorConcentration: { sector: string; exposure: number; percentage: number }[];
  geographicConcentration: { state: string; exposure: number; percentage: number }[];
}

// ── Document Versioning & E-Signature Types ────────────

export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  changeNote?: string;
  uploadedBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export type SignatureStatus =
  'PENDING' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
export type SignerStatus = 'PENDING' | 'VIEWED' | 'SIGNED' | 'DECLINED';

export interface SignatureRequest {
  id: string;
  documentId: string;
  loanId?: string;
  status: SignatureStatus;
  requestedBy?: { id: string; firstName: string; lastName: string };
  signers: SignatureSigner[];
  message?: string;
  expiresAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface SignatureSigner {
  id: string;
  signerName: string;
  signerEmail: string;
  signerRole?: string;
  status: SignerStatus;
  signedAt?: string;
  viewedAt?: string;
  declinedAt?: string;
  declineReason?: string;
}

// ── Analytics Types ────────────────────────────────────

export interface CashflowForecast {
  month: string;
  expectedPrincipal: number;
  expectedInterest: number;
  expectedFees: number;
  total: number;
  loanCount: number;
}

export interface MaturityCalendarEntry {
  month: string;
  loans: {
    id: string;
    loanNumber: string;
    borrowerName: string;
    principal: number;
    maturityDate: string;
  }[];
  totalAmount: number;
  count: number;
}

export interface LenderExposure {
  lenderId: string;
  lenderName: string;
  totalExposure: number;
  loanCount: number;
  limit?: number;
  utilisation: number;
}

export interface TrendDataPoint {
  period: string;
  current: number;
  previous: number;
  change: number;
  changePercent: number;
}

export interface KeyMetrics {
  weightedAvgRate: number;
  totalAUM: number;
  avgLTV: number;
  netInterestMargin: number;
  costOfFunds: number;
  arrearsRatio: number;
  activeLoans: number;
  avgLoanSize: number;
}

// ── Scheduled Reports & Alerts Types ────────────────────

export type AlertRuleType =
  | 'LVR_BREACH'
  | 'MATURITY_APPROACHING'
  | 'PAYMENT_OVERDUE'
  | 'RATE_CHANGE'
  | 'COVENANT_BREACH'
  | 'INSURANCE_EXPIRY'
  | 'VALUATION_STALE'
  | 'CUSTOM';

export type AlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export const ALERT_SEVERITY_CONFIG: Record<
  AlertSeverity,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  LOW: { label: 'Low', color: '#6b7280', bgColor: '#f9fafb', icon: 'info' },
  MEDIUM: { label: 'Medium', color: '#f59e0b', bgColor: '#fffbeb', icon: 'alert-triangle' },
  HIGH: { label: 'High', color: '#ef4444', bgColor: '#fef2f2', icon: 'alert-circle' },
  CRITICAL: { label: 'Critical', color: '#991b1b', bgColor: '#fef2f2', icon: 'x-octagon' },
};

export interface ScheduledReport {
  id: string;
  name: string;
  reportType: string;
  reportConfig: any;
  cronExpression: string;
  recipients: string[];
  format: string;
  isActive: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface AlertRule {
  id: string;
  name: string;
  ruleType: AlertRuleType;
  conditions: any;
  recipients: string[];
  escalationChain?: any;
  isActive: boolean;
  lastTriggeredAt?: string;
  triggerCount: number;
  createdAt: string;
}

export interface Alert {
  id: string;
  ruleId: string;
  rule?: AlertRule;
  loanId?: string;
  loanNumber?: string;
  borrowerName?: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  data?: any;
  acknowledged: boolean;
  acknowledgedBy?: { id: string; firstName: string; lastName: string };
  acknowledgedAt?: string;
  createdAt: string;
}

export interface AlertStats {
  total: number;
  unacknowledged: number;
  bySeverity: Record<AlertSeverity, number>;
  byType: Record<AlertRuleType, number>;
}

// ── Fee Schedule Types ────────────────────────────────────

export type FeeType =
  | 'ESTABLISHMENT'
  | 'LINE_FEE'
  | 'MONTHLY_SERVICE'
  | 'LATE_PAYMENT'
  | 'DISCHARGE'
  | 'VARIATION'
  | 'EXTENSION'
  | 'VALUATION'
  | 'LEGAL'
  | 'INSURANCE'
  | 'DEFAULT_INTEREST'
  | 'CUSTOM';

export type FeeFrequency = 'ONE_OFF' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
export type FeeChargeStatus = 'PENDING' | 'CHARGED' | 'PAID' | 'WAIVED' | 'DEFERRED' | 'CANCELLED';

export const FEE_TYPE_LABELS: Record<FeeType, string> = {
  ESTABLISHMENT: 'Establishment Fee',
  LINE_FEE: 'Line Fee',
  MONTHLY_SERVICE: 'Monthly Service',
  LATE_PAYMENT: 'Late Payment',
  DISCHARGE: 'Discharge Fee',
  VARIATION: 'Variation Fee',
  EXTENSION: 'Extension Fee',
  VALUATION: 'Valuation Fee',
  LEGAL: 'Legal Fee',
  INSURANCE: 'Insurance',
  DEFAULT_INTEREST: 'Default Interest',
  CUSTOM: 'Custom Fee',
};

export interface FeeSchedule {
  id: string;
  loanId: string;
  feeType: FeeType;
  name: string;
  amount: string;
  gstAmount: string;
  totalAmount: string;
  frequency: FeeFrequency;
  nextDueDate?: string;
  lastChargedDate?: string;
  isActive: boolean;
  autoCharge: boolean;
  waivedAt?: string;
  waivedReason?: string;
  notes?: string;
  createdAt: string;
}

export interface FeeCharge {
  id: string;
  feeScheduleId: string;
  loanId: string;
  amount: string;
  gstAmount: string;
  totalAmount: string;
  status: FeeChargeStatus;
  dueDate: string;
  paidDate?: string;
  waivedAt?: string;
  waivedReason?: string;
  createdAt: string;
  feeSchedule?: {
    id: string;
    name: string;
    feeType: FeeType;
  };
}

export interface FeeSummary {
  totalFees: number;
  activeFees: number;
  totalPaid: number;
  totalOutstanding: number;
  totalWaived: number;
  outstandingCharges: number;
  feeCount: number;
  chargeCount: number;
}

// ── Email Integration Types ────────────────────────────────

export type EmailDirection = 'INBOUND' | 'OUTBOUND';
export type EmailMessageStatus = 'DRAFT' | 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';
export type EmailThreadStatus = 'ACTIVE' | 'ARCHIVED' | 'CLOSED';
export type EmailThreadCategory = 'GENERAL' | 'OFFER_LETTER';

export interface EmailThread {
  id: string;
  tenantId?: string;
  loanId?: string;
  borrowerId?: string;
  subject: string;
  category?: EmailThreadCategory;
  loanNumber?: string;
  legalMatterRef?: string;
  participants: { email: string; name: string; role?: string }[];
  status: EmailThreadStatus;
  lastMessageAt?: string;
  messages?: EmailMessage[];
  messageCount?: number;
  createdAt: string;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  direction: EmailDirection;
  fromAddress: string;
  fromName?: string;
  toAddresses: { email: string; name?: string }[];
  ccAddresses?: { email: string; name?: string }[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  hasAttachments: boolean;
  attachments?: {
    name: string;
    size: number;
    mimeType: string;
    storageKey?: string;
    url?: string;
  }[];
  isRead: boolean;
  isStarred: boolean;
  sentAt?: string;
  receivedAt?: string;
  openedAt?: string;
  clickedAt?: string;
  status: EmailMessageStatus;
  sentBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface EmailConfig {
  id?: string;
  tenantId: string;
  clientId: string;
  sharedMailbox: string;
  isActive: boolean;
  lastSyncAt?: string;
}

// ── Regulatory Return Types ────────────────────────────────

export type RegulatoryReturnType =
  | 'AFSL_ANNUAL'
  | 'AML_CTF_REPORT'
  | 'BREACH_NOTIFICATION'
  | 'CREDIT_ACTIVITY'
  | 'COMPLAINTS_REPORT'
  | 'HARDSHIP_REPORT'
  | 'TRUST_ACCOUNT';

export type RegulatoryReturnStatus = 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'SUBMITTED' | 'OVERDUE';

export const REGULATORY_RETURN_LABELS: Record<RegulatoryReturnType, string> = {
  AFSL_ANNUAL: 'AFSL Annual Return',
  AML_CTF_REPORT: 'AML/CTF Compliance Report',
  BREACH_NOTIFICATION: 'Breach Notification',
  CREDIT_ACTIVITY: 'Credit Activity Return',
  COMPLAINTS_REPORT: 'Complaints Report (IDR)',
  HARDSHIP_REPORT: 'Hardship Assistance Report',
  TRUST_ACCOUNT: 'Trust Account Reconciliation',
};

export const REGULATORY_STATUS_CONFIG: Record<
  RegulatoryReturnStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  IN_REVIEW: { label: 'In Review', color: '#f59e0b', bgColor: '#fffbeb' },
  APPROVED: { label: 'Approved', color: '#10b981', bgColor: '#ecfdf5' },
  SUBMITTED: { label: 'Submitted', color: '#6366f1', bgColor: '#eef2ff' },
  OVERDUE: { label: 'Overdue', color: '#ef4444', bgColor: '#fef2f2' },
};

export interface RegulatoryReturn {
  id: string;
  returnType: RegulatoryReturnType;
  periodStart: string;
  periodEnd: string;
  status: RegulatoryReturnStatus;
  data: any;
  summary?: any;
  generatedBy?: { id: string; firstName: string; lastName: string };
  submittedAt?: string;
  submittedRef?: string;
  dueDate?: string;
  notes?: string;
  createdAt: string;
}

// ── Data Export Types ────────────────────────────────────

export type DataExportType =
  | 'LOANS'
  | 'BORROWERS'
  | 'TRANSACTIONS'
  | 'REPAYMENTS'
  | 'SECURITIES'
  | 'FEES'
  | 'COMMISSIONS'
  | 'COMPLIANCE'
  | 'CUSTOM_SQL';

export type ExportFormat = 'CSV' | 'XLSX' | 'JSON' | 'PDF';
export type DataExportStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';

export interface DataExport {
  id: string;
  name: string;
  exportType: DataExportType;
  format: ExportFormat;
  filters?: any;
  columns?: string[];
  schedule?: string;
  isRecurring: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  rowCount?: number;
  fileSize?: number;
  status: DataExportStatus;
  createdBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface ExecutiveSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  data: {
    portfolioOverview: any;
    financialPerformance: any;
    riskMetrics: any;
    pipeline: any;
    compliance: any;
    topLoans: any[];
    referrerPerformance: any[];
  };
  generatedBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

// ── Document Manager Types ────────────────────────────────

export type DocumentCategory =
  | 'LOAN_AGREEMENT'
  | 'SECURITY_DOCUMENTS'
  | 'VALUATION'
  | 'INSURANCE'
  | 'BORROWER_ID'
  | 'FINANCIAL_STATEMENTS'
  | 'LEGAL_ADVICE'
  | 'CORRESPONDENCE'
  | 'SETTLEMENT'
  | 'DISCHARGE'
  | 'APPLICATION'
  | 'IDENTITY'
  | 'FINANCIAL'
  | 'COLLATERAL'
  | 'AGREEMENT'
  | 'OTHER';

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  LOAN_AGREEMENT: 'Loan Agreement',
  SECURITY_DOCUMENTS: 'Security Documents',
  VALUATION: 'Valuation',
  INSURANCE: 'Insurance',
  BORROWER_ID: 'Borrower ID',
  FINANCIAL_STATEMENTS: 'Financial Statements',
  LEGAL_ADVICE: 'Legal Advice',
  CORRESPONDENCE: 'Correspondence',
  SETTLEMENT: 'Settlement',
  DISCHARGE: 'Discharge',
  APPLICATION: 'Application Forms',
  IDENTITY: 'Identity Documents',
  FINANCIAL: 'Financial Statements',
  COLLATERAL: 'Collateral Documents',
  AGREEMENT: 'Loan Agreements',
  OTHER: 'Other',
};

export interface DocumentWithVersions {
  id: string;
  name: string;
  category?: DocumentCategory;
  versionCount: number;
  latestVersion?: DocumentVersion;
  signatureStatus?: SignatureStatus;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentChecklistItem {
  category: DocumentCategory;
  label: string;
  required: boolean;
  completed: boolean;
  documentId?: string;
  documentName?: string;
  lastUpdated?: string;
}

// ── Feature Request & Bug Report Types ────────────────────

export type FeatureCategory =
  | 'UI_UX'
  | 'REPORTING'
  | 'AUTOMATION'
  | 'COMPLIANCE'
  | 'INTEGRATION'
  | 'PERFORMANCE'
  | 'SECURITY'
  | 'WORKFLOW'
  | 'DATA_MANAGEMENT'
  | 'OTHER';

export type FeaturePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type FeatureStatus =
  'SUBMITTED' | 'UNDER_REVIEW' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'DECLINED' | 'DUPLICATE';
export type FeedbackSource = 'USER_SUBMITTED' | 'AI_DETECTED' | 'ADMIN_CREATED' | 'CHAT_ANALYSIS';

export const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  UI_UX: 'UI / UX',
  REPORTING: 'Reporting',
  AUTOMATION: 'Automation',
  COMPLIANCE: 'Compliance',
  INTEGRATION: 'Integration',
  PERFORMANCE: 'Performance',
  SECURITY: 'Security',
  WORKFLOW: 'Workflow',
  DATA_MANAGEMENT: 'Data Management',
  OTHER: 'Other',
};

export const FEATURE_STATUS_CONFIG: Record<
  FeatureStatus,
  { label: string; color: string; bgColor: string }
> = {
  SUBMITTED: { label: 'Submitted', color: '#6b7280', bgColor: '#f9fafb' },
  UNDER_REVIEW: { label: 'Under Review', color: '#f59e0b', bgColor: '#fffbeb' },
  PLANNED: { label: 'Planned', color: '#3b82f6', bgColor: '#eff6ff' },
  IN_PROGRESS: { label: 'In Progress', color: '#8b5cf6', bgColor: '#f5f3ff' },
  COMPLETED: { label: 'Completed', color: '#22c55e', bgColor: '#f0fdf4' },
  DECLINED: { label: 'Declined', color: '#ef4444', bgColor: '#fef2f2' },
  DUPLICATE: { label: 'Duplicate', color: '#9ca3af', bgColor: '#f9fafb' },
};

export const FEATURE_PRIORITY_CONFIG: Record<FeaturePriority, { label: string; color: string }> = {
  LOW: { label: 'Low', color: '#6b7280' },
  MEDIUM: { label: 'Medium', color: '#f59e0b' },
  HIGH: { label: 'High', color: '#ef4444' },
  CRITICAL: { label: 'Critical', color: '#991b1b' },
};

export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: FeatureCategory;
  priority: FeaturePriority;
  status: FeatureStatus;
  source: FeedbackSource;
  requestedBy?: { id: string; firstName: string; lastName: string };
  assignedTo?: { id: string; firstName: string; lastName: string };
  screenshots?: { filename: string; storageKey: string; mimeType: string }[];
  upvotes: number;
  voters?: string[];
  adminNotes?: string;
  estimatedEffort?: string;
  targetRelease?: string;
  completedAt?: string;
  commentCount?: number;
  comments?: FeatureComment[];
  createdAt: string;
  updatedAt: string;
}

export interface FeatureComment {
  id: string;
  featureId: string;
  author?: { id: string; firstName: string; lastName: string };
  body: string;
  isInternal: boolean;
  createdAt: string;
}

export type BugSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'BLOCKER';
export type BugStatus =
  'OPEN' | 'CONFIRMED' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'WONT_FIX' | 'DUPLICATE';

export const BUG_SEVERITY_CONFIG: Record<
  BugSeverity,
  { label: string; color: string; bgColor: string }
> = {
  LOW: { label: 'Low', color: '#6b7280', bgColor: '#f9fafb' },
  MEDIUM: { label: 'Medium', color: '#f59e0b', bgColor: '#fffbeb' },
  HIGH: { label: 'High', color: '#ef4444', bgColor: '#fef2f2' },
  CRITICAL: { label: 'Critical', color: '#991b1b', bgColor: '#fef2f2' },
  BLOCKER: { label: 'Blocker', color: '#7f1d1d', bgColor: '#fef2f2' },
};

export const BUG_STATUS_CONFIG: Record<
  BugStatus,
  { label: string; color: string; bgColor: string }
> = {
  OPEN: { label: 'Open', color: '#ef4444', bgColor: '#fef2f2' },
  CONFIRMED: { label: 'Confirmed', color: '#f59e0b', bgColor: '#fffbeb' },
  IN_PROGRESS: { label: 'In Progress', color: '#8b5cf6', bgColor: '#f5f3ff' },
  RESOLVED: { label: 'Resolved', color: '#22c55e', bgColor: '#f0fdf4' },
  CLOSED: { label: 'Closed', color: '#6b7280', bgColor: '#f9fafb' },
  WONT_FIX: { label: "Won't Fix", color: '#9ca3af', bgColor: '#f9fafb' },
  DUPLICATE: { label: 'Duplicate', color: '#9ca3af', bgColor: '#f9fafb' },
};

export interface BugReport {
  id: string;
  title: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehaviour?: string;
  actualBehaviour?: string;
  severity: BugSeverity;
  status: BugStatus;
  source: FeedbackSource;
  reportedBy?: { id: string; firstName: string; lastName: string };
  assignedTo?: { id: string; firstName: string; lastName: string };
  pageUrl?: string;
  browserInfo?: string;
  screenshots?: { filename: string; storageKey: string; mimeType: string }[];
  resolution?: string;
  resolvedAt?: string;
  commentCount?: number;
  comments?: BugComment[];
  createdAt: string;
  updatedAt: string;
}

export interface BugComment {
  id: string;
  bugId: string;
  author?: { id: string; firstName: string; lastName: string };
  body: string;
  isInternal: boolean;
  createdAt: string;
}

export type ChatAnalysisStatus = 'PENDING' | 'ANALYSED' | 'REVIEWED' | 'DISMISSED';

export interface ChatSession {
  id: string;
  userId: string;
  user?: { id: string; firstName: string; lastName: string; email: string };
  title?: string;
  loanId?: string;
  loan?: { id: string; loanNumber: string };
  messageCount: number;
  analysisStatus: ChatAnalysisStatus;
  detectedIntents?: { intent: string; confidence: number; description: string }[];
  suggestedFeatures?: { title: string; description: string; category: string }[];
  sentiment?: string;
  startedAt: string;
  lastMessageAt?: string;
  endedAt?: string;
}

export interface ChatAnalysisSummary {
  totalSessions: number;
  pendingAnalysis: number;
  commonIntents: { intent: string; count: number }[];
  sentimentDistribution: Record<string, number>;
  topSuggestedFeatures: { title: string; count: number }[];
}

export interface FeatureStats {
  byStatus: Record<FeatureStatus, number>;
  byCategory: Record<FeatureCategory, number>;
  byPriority: Record<FeaturePriority, number>;
  total: number;
}

export interface BugStats {
  byStatus: Record<BugStatus, number>;
  bySeverity: Record<BugSeverity, number>;
  total: number;
}

// ── Trust Account Types ─────────────────────────────────

export type TrustAccountType = 'STATUTORY' | 'CONTROLLED_MONEY' | 'GENERAL';
export type TrustTransactionType =
  'RECEIPT' | 'DISBURSEMENT' | 'TRANSFER' | 'INTEREST' | 'ADJUSTMENT';
export type TrustTransactionStatus =
  'PENDING' | 'MATCHED' | 'UNMATCHED' | 'DISPUTED' | 'RECONCILED';
export type ReconciliationStatus =
  'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'SIGNED_OFF' | 'DISPUTED';

export const TRUST_ACCOUNT_TYPE_LABELS: Record<TrustAccountType, string> = {
  STATUTORY: 'Statutory',
  CONTROLLED_MONEY: 'Controlled Money',
  GENERAL: 'General',
};

export const TRUST_TX_STATUS_CONFIG: Record<
  TrustTransactionStatus,
  { label: string; color: string; bgColor: string }
> = {
  PENDING: { label: 'Pending', color: '#f59e0b', bgColor: '#fffbeb' },
  MATCHED: { label: 'Matched', color: '#22c55e', bgColor: '#f0fdf4' },
  UNMATCHED: { label: 'Unmatched', color: '#ef4444', bgColor: '#fef2f2' },
  DISPUTED: { label: 'Disputed', color: '#991b1b', bgColor: '#fef2f2' },
  RECONCILED: { label: 'Reconciled', color: '#3b82f6', bgColor: '#eff6ff' },
};

export const RECON_STATUS_CONFIG: Record<
  ReconciliationStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  IN_PROGRESS: { label: 'In Progress', color: '#f59e0b', bgColor: '#fffbeb' },
  COMPLETED: { label: 'Completed', color: '#22c55e', bgColor: '#f0fdf4' },
  SIGNED_OFF: { label: 'Signed Off', color: '#3b82f6', bgColor: '#eff6ff' },
  DISPUTED: { label: 'Disputed', color: '#ef4444', bgColor: '#fef2f2' },
};

export interface TrustAccount {
  id: string;
  name: string;
  accountNumber: string;
  bsb: string;
  bankName: string;
  accountType: TrustAccountType;
  openingBalance: number;
  currentBalance: number;
  isActive: boolean;
  lastReconciledAt?: string;
  notes?: string;
  createdAt: string;
}

export interface TrustTransaction {
  id: string;
  trustAccountId: string;
  loanId?: string;
  type: TrustTransactionType;
  status: TrustTransactionStatus;
  amount: number;
  gst: number;
  reference?: string;
  description?: string;
  payerPayee?: string;
  transactionDate: string;
  matchConfidence?: number;
  reconciledAt?: string;
  loan?: { id: string; loanNumber: string };
  reconciledBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface BankStatement {
  id: string;
  trustAccountId: string;
  filename: string;
  periodStart: string;
  periodEnd: string;
  openingBalance: number;
  closingBalance: number;
  totalDebits: number;
  totalCredits: number;
  lineCount: number;
  matchedCount: number;
  unmatchedCount: number;
  uploadedBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface BankStatementLine {
  id: string;
  lineNumber: number;
  transactionDate: string;
  description: string;
  reference?: string;
  debit?: number;
  credit?: number;
  balance?: number;
  isMatched: boolean;
}

export interface TrustReconciliation {
  id: string;
  trustAccountId: string;
  periodStart: string;
  periodEnd: string;
  status: ReconciliationStatus;
  openingBalance: number;
  closingBalance: number;
  bankBalance: number;
  bookBalance: number;
  variance: number;
  totalReceipts: number;
  totalDisbursements: number;
  matchedItems: number;
  unmatchedItems: number;
  preparedBy?: { id: string; firstName: string; lastName: string };
  signedOffBy?: { id: string; firstName: string; lastName: string };
  signedOffAt?: string;
  notes?: string;
  createdAt: string;
}

// ── Delegated Authority Types ───────────────────────────

export type AuthorityType =
  | 'LOAN_APPROVAL'
  | 'DISBURSEMENT'
  | 'FEE_WAIVER'
  | 'RESTRUCTURE'
  | 'WRITE_OFF'
  | 'SETTLEMENT'
  | 'VARIATION';

export const AUTHORITY_TYPE_LABELS: Record<AuthorityType, string> = {
  LOAN_APPROVAL: 'Loan Approval',
  DISBURSEMENT: 'Disbursement',
  FEE_WAIVER: 'Fee Waiver',
  RESTRUCTURE: 'Restructure',
  WRITE_OFF: 'Write Off',
  SETTLEMENT: 'Settlement',
  VARIATION: 'Variation',
};

export interface DelegatedAuthority {
  id: string;
  userId: string;
  authorityType: AuthorityType;
  maxAmount?: number;
  maxLvr?: number;
  loanTypes?: string[];
  requiresDualSign: boolean;
  isActive: boolean;
  grantedAt: string;
  expiresAt?: string;
  notes?: string;
  user?: { id: string; firstName: string; lastName: string; role: string };
  grantedBy?: { id: string; firstName: string; lastName: string };
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  leaderId?: string;
  isActive: boolean;
  leader?: { id: string; firstName: string; lastName: string };
  members?: TeamMember[];
  _count?: { members: number };
  createdAt: string;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: string;
  joinedAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string; role: string };
}

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  config?: any;
}

// ── Loan Origination Types ──────────────────────────────

export type ApplicationStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'IN_REVIEW'
  | 'ADDITIONAL_INFO_REQUIRED'
  | 'CREDIT_ASSESSMENT'
  | 'APPROVED'
  | 'CONDITIONALLY_APPROVED'
  | 'DECLINED'
  | 'WITHDRAWN'
  | 'CONVERTED';
export type ApplicationSource =
  'DIRECT' | 'BROKER' | 'WEBSITE' | 'REFERRAL' | 'REPEAT_CLIENT' | 'OTHER';

export const APPLICATION_STATUS_CONFIG: Record<
  ApplicationStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  SUBMITTED: { label: 'Submitted', color: '#3b82f6', bgColor: '#eff6ff' },
  IN_REVIEW: { label: 'In Review', color: '#f59e0b', bgColor: '#fffbeb' },
  ADDITIONAL_INFO_REQUIRED: { label: 'Info Required', color: '#f97316', bgColor: '#fff7ed' },
  CREDIT_ASSESSMENT: { label: 'Credit Assessment', color: '#8b5cf6', bgColor: '#f5f3ff' },
  APPROVED: { label: 'Approved', color: '#22c55e', bgColor: '#f0fdf4' },
  CONDITIONALLY_APPROVED: { label: 'Conditional', color: '#10b981', bgColor: '#ecfdf5' },
  DECLINED: { label: 'Declined', color: '#ef4444', bgColor: '#fef2f2' },
  WITHDRAWN: { label: 'Withdrawn', color: '#9ca3af', bgColor: '#f9fafb' },
  CONVERTED: { label: 'Converted', color: '#059669', bgColor: '#ecfdf5' },
};

export interface ApplicationStatusHistoryEntry {
  at?: string;
  status?: string;
  action?: string;
  detail?: string;
  actorId?: string;
  actorLabel?: string;
}

export interface LoanApplication {
  id: string;
  applicationNumber: string;
  status: ApplicationStatus;
  source: ApplicationSource;
  applicantName: string;
  applicantEmail?: string;
  applicantPhone?: string;
  applicantEntityType?: string;
  applicantAbn?: string;
  applicantAcn?: string;
  requestedAmount: number;
  loanPurpose?: string;
  loanType?: string;
  requestedTerm?: number;
  preferredRate?: number;
  securityAddress?: string;
  securityType?: string;
  estimatedValue?: number;
  estimatedLvr?: number;
  annualIncome?: number;
  existingDebts?: number;
  monthlyExpenses?: number;
  netAssets?: number;
  referrerId?: string;
  brokerReference?: string;
  assignedTo?: { id: string; firstName: string; lastName: string };
  reviewedBy?: { id: string; firstName: string; lastName: string };
  declineReason?: string;
  convertedLoanId?: string;
  documents?: { name: string; storageKey: string; mimeType: string }[];
  /** Structured requests (e.g. requiredItems) when status is ADDITIONAL_INFO_REQUIRED */
  additionalInfo?: { requiredItems?: string[] } | Record<string, unknown>;
  applicationSecurities?: ApplicationSecurity[];
  applicationGuarantors?: ApplicationGuarantor[];
  applicationTerms?: ApplicationTerms | null;
  applicationFees?: ApplicationFee[];
  statusHistory?: ApplicationStatusHistoryEntry[] | string;
  notes?: string;
  submittedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationSecurity {
  id: string;
  securityKind: string;
  registrationStatus: string;
  propertyAddress?: string;
  propertyValue?: number;
  priority?: number;
  priorEncumbrances?: number;
  existingDebt?: number;
  existingDebtHolder?: string;
  borrowerValuation?: number;
  internalEstimate?: number;
  valuerEstimate?: number;
  valuationDate?: string;
  valuerName?: string;
  entityName?: string;
  entityIdentifierType?: string;
  entityIdentifierValue?: string;
  entityAcn?: string;
  entityAbn?: string;
  entityTrustName?: string;
  triggerCondition?: string;
  triggerConditionOther?: string;
  escrowInstrument?: string;
  notes?: string;
}

export interface ApplicationGuarantor {
  id: string;
  name: string;
  entityType: string;
  identifierType?: string;
  identifierValue?: string;
  abn?: string;
  acn?: string;
  trustName?: string;
  trustAbn?: string;
  email?: string;
  phone?: string;
  guaranteeType?: string;
  limitOfLiability?: number;
  guaranteeDate?: string;
  expiryDate?: string;
  notes?: string;
}

export interface ApplicationTerms {
  id: string;
  loanAmountMode?: string;
  repaymentType?: string;
  paymentFrequency?: string;
  startDate?: string;
  maturityDate?: string;
  interestRate?: number | string;
  defaultRate?: number | string;
  rolloverOption?: boolean;
  netToBorrower?: number | string;
  prepaidInterest?: number | string;
  cancellationFee?: number | string;
  valuationFee?: number | string;
  valuationFeeTbc?: boolean;
  offerValidityDays?: number;
  totalFees?: number | string;
  totalFacility?: number | string;
  periodicPayment?: number | string;
  totalPayment?: number | string;
  totalInterest?: number | string;
  totalSecurityValue?: number | string;
  lvr?: number | string;
  totalLvr?: number | string;
  lvr10Drop?: number | string;
  lvr20Drop?: number | string;
}

export interface ApplicationFee {
  id: string;
  name: string;
  mode: string;
  value: number | string;
  amount: number | string;
  sortOrder?: number;
}

// ── Board Pack Types ────────────────────────────────────

export type BoardPackStatus = 'DRAFT' | 'GENERATING' | 'REVIEW' | 'APPROVED' | 'DISTRIBUTED';
export type ReportSectionType =
  | 'PORTFOLIO_SUMMARY'
  | 'LOAN_ARREARS'
  | 'NEW_LOANS'
  | 'MATURITY_SCHEDULE'
  | 'RISK_ANALYSIS'
  | 'FINANCIAL_PERFORMANCE'
  | 'PIPELINE_SUMMARY'
  | 'COMPLIANCE_SUMMARY'
  | 'TRUST_ACCOUNT_SUMMARY'
  | 'COLLECTIONS_SUMMARY'
  | 'CUSTOM_TEXT'
  | 'CUSTOM_TABLE'
  | 'CUSTOM_CHART';

export const BOARD_PACK_STATUS_CONFIG: Record<
  BoardPackStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  GENERATING: { label: 'Generating', color: '#f59e0b', bgColor: '#fffbeb' },
  REVIEW: { label: 'In Review', color: '#8b5cf6', bgColor: '#f5f3ff' },
  APPROVED: { label: 'Approved', color: '#22c55e', bgColor: '#f0fdf4' },
  DISTRIBUTED: { label: 'Distributed', color: '#3b82f6', bgColor: '#eff6ff' },
};

export const REPORT_SECTION_LABELS: Record<ReportSectionType, string> = {
  PORTFOLIO_SUMMARY: 'Portfolio Summary',
  LOAN_ARREARS: 'Loan Arrears',
  NEW_LOANS: 'New Loans',
  MATURITY_SCHEDULE: 'Maturity Schedule',
  RISK_ANALYSIS: 'Risk Analysis',
  FINANCIAL_PERFORMANCE: 'Financial Performance',
  PIPELINE_SUMMARY: 'Pipeline Summary',
  COMPLIANCE_SUMMARY: 'Compliance Summary',
  TRUST_ACCOUNT_SUMMARY: 'Trust Account Summary',
  COLLECTIONS_SUMMARY: 'Collections Summary',
  CUSTOM_TEXT: 'Custom Text',
  CUSTOM_TABLE: 'Custom Table',
  CUSTOM_CHART: 'Custom Chart',
};

export interface BoardPackTemplate {
  id: string;
  name: string;
  description?: string;
  sections: { type: ReportSectionType; title: string; config?: any; order: number }[];
  isDefault: boolean;
  frequency?: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface BoardPack {
  id: string;
  templateId: string;
  title: string;
  periodStart: string;
  periodEnd: string;
  status: BoardPackStatus;
  data?: any;
  generatedAt?: string;
  generatedBy?: { id: string; firstName: string; lastName: string };
  approvedBy?: { id: string; firstName: string; lastName: string };
  approvedAt?: string;
  distributedAt?: string;
  distributionList?: string[];
  notes?: string;
  template?: BoardPackTemplate;
  createdAt: string;
}

// ── Dashboard Widget Types ──────────────────────────────

export type WidgetType =
  | 'PORTFOLIO_HEATMAP'
  | 'ARREARS_AGING'
  | 'PIPELINE_FUNNEL'
  | 'MATURITY_CALENDAR'
  | 'CASHFLOW_FORECAST'
  | 'LOAN_STATUS_DIST'
  | 'LENDER_EXPOSURE'
  | 'RATE_DISTRIBUTION'
  | 'NEW_BUSINESS_TREND'
  | 'COLLECTION_STATUS'
  | 'KPI_CARD'
  | 'CUSTOM_QUERY';

export const WIDGET_TYPE_LABELS: Record<WidgetType, string> = {
  PORTFOLIO_HEATMAP: 'Portfolio Heatmap',
  ARREARS_AGING: 'Arrears Aging',
  PIPELINE_FUNNEL: 'Pipeline Funnel',
  MATURITY_CALENDAR: 'Maturity Calendar',
  CASHFLOW_FORECAST: 'Cashflow Forecast',
  LOAN_STATUS_DIST: 'Loan Status Distribution',
  LENDER_EXPOSURE: 'Lender Exposure',
  RATE_DISTRIBUTION: 'Rate Distribution',
  NEW_BUSINESS_TREND: 'New Business Trend',
  COLLECTION_STATUS: 'Collection Status',
  KPI_CARD: 'KPI Card',
  CUSTOM_QUERY: 'Custom Query',
};

export interface DashboardWidget {
  id: string;
  widgetType: WidgetType;
  title: string;
  position: { x: number; y: number; w: number; h: number };
  config?: any;
  isVisible: boolean;
  refreshInterval?: number;
}

// ── QA Tracker Types ────────────────────────────────────

export type QaIssueStatus =
  | 'OPEN'
  | 'INVESTIGATING'
  | 'IN_PROGRESS'
  | 'FIXED'
  | 'VERIFIED'
  | 'CLOSED'
  | 'WONT_FIX'
  | 'DUPLICATE';
export type QaIssueSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type QaIssueSource =
  'AUTOMATED_TEST' | 'MANUAL_TEST' | 'USER_REPORT' | 'FIX_AGENT' | 'MONITORING';
export type QaResolutionType =
  | 'CODE_FIX'
  | 'CONFIG_CHANGE'
  | 'DATA_FIX'
  | 'SCHEMA_MIGRATION'
  | 'DEPENDENCY_UPDATE'
  | 'NOT_A_BUG'
  | 'DUPLICATE_CLOSED'
  | 'WONT_FIX_ACCEPTED';

export const QA_STATUS_CONFIG: Record<
  QaIssueStatus,
  { label: string; color: string; bgColor: string }
> = {
  OPEN: { label: 'Open', color: '#ef4444', bgColor: '#fef2f2' },
  INVESTIGATING: { label: 'Investigating', color: '#f59e0b', bgColor: '#fffbeb' },
  IN_PROGRESS: { label: 'In Progress', color: '#8b5cf6', bgColor: '#f5f3ff' },
  FIXED: { label: 'Fixed', color: '#22c55e', bgColor: '#f0fdf4' },
  VERIFIED: { label: 'Verified', color: '#3b82f6', bgColor: '#eff6ff' },
  CLOSED: { label: 'Closed', color: '#6b7280', bgColor: '#f9fafb' },
  WONT_FIX: { label: "Won't Fix", color: '#9ca3af', bgColor: '#f9fafb' },
  DUPLICATE: { label: 'Duplicate', color: '#9ca3af', bgColor: '#f9fafb' },
};

export const QA_SEVERITY_CONFIG: Record<
  QaIssueSeverity,
  { label: string; color: string; bgColor: string; slaHours: number }
> = {
  CRITICAL: { label: 'Critical', color: '#7f1d1d', bgColor: '#fef2f2', slaHours: 4 },
  HIGH: { label: 'High', color: '#ef4444', bgColor: '#fef2f2', slaHours: 8 },
  MEDIUM: { label: 'Medium', color: '#f59e0b', bgColor: '#fffbeb', slaHours: 24 },
  LOW: { label: 'Low', color: '#6b7280', bgColor: '#f9fafb', slaHours: 72 },
};

export interface QaIssue {
  id: string;
  issueNumber: number;
  title: string;
  description: string;
  severity: QaIssueSeverity;
  status: QaIssueStatus;
  source: QaIssueSource;
  testSuite?: string;
  testName?: string;
  testPhase?: number;
  errorMessage?: string;
  commitSha?: string;
  githubIssueUrl?: string;
  githubPrUrl?: string;
  resolutionType?: QaResolutionType;
  resolutionSummary?: string;
  fixDescription?: string;
  filesChanged?: string[];
  assignedTo?: { id: string; firstName: string; lastName: string };
  resolvedBy?: { id: string; firstName: string; lastName: string };
  resolvedAt?: string;
  verifiedAt?: string;
  affectedModule?: string;
  affectedEndpoint?: string;
  environment: string;
  slaDeadline?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

// ── Bulk Communication Types ────────────────────────────

export type BulkCommStatus =
  'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'SENDING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
export type BulkCommChannel = 'EMAIL' | 'SMS' | 'IN_APP';

export const BULK_COMM_STATUS_CONFIG: Record<
  BulkCommStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  PENDING_APPROVAL: { label: 'Pending Approval', color: '#f59e0b', bgColor: '#fffbeb' },
  APPROVED: { label: 'Approved', color: '#22c55e', bgColor: '#f0fdf4' },
  SENDING: { label: 'Sending...', color: '#3b82f6', bgColor: '#eff6ff' },
  COMPLETED: { label: 'Completed', color: '#059669', bgColor: '#ecfdf5' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bgColor: '#f9fafb' },
  FAILED: { label: 'Failed', color: '#ef4444', bgColor: '#fef2f2' },
};

export interface BulkCommunication {
  id: string;
  name: string;
  channel: BulkCommChannel;
  status: BulkCommStatus;
  selectionType: string;
  subject?: string;
  body: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  openedCount: number;
  requiresApproval: boolean;
  approvedBy?: { id: string; firstName: string; lastName: string };
  approvedAt?: string;
  scheduledAt?: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface BulkCommRecipient {
  id: string;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  status: string;
  sentAt?: string;
  errorMessage?: string;
}

// ── Data Migration Types ────────────────────────────────

export type MigrationStatus =
  | 'DRAFT'
  | 'VALIDATING'
  | 'VALIDATED'
  | 'DRY_RUN'
  | 'IMPORTING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ROLLED_BACK';
export type MigrationEntityType =
  | 'LOANS'
  | 'BORROWERS'
  | 'SECURITIES'
  | 'TRANSACTIONS'
  | 'REPAYMENT_SCHEDULES'
  | 'GUARANTORS'
  | 'REFERRERS';

export const MIGRATION_STATUS_CONFIG: Record<
  MigrationStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: { label: 'Draft', color: '#6b7280', bgColor: '#f9fafb' },
  VALIDATING: { label: 'Validating', color: '#f59e0b', bgColor: '#fffbeb' },
  VALIDATED: { label: 'Validated', color: '#22c55e', bgColor: '#f0fdf4' },
  DRY_RUN: { label: 'Dry Run', color: '#8b5cf6', bgColor: '#f5f3ff' },
  IMPORTING: { label: 'Importing', color: '#3b82f6', bgColor: '#eff6ff' },
  COMPLETED: { label: 'Completed', color: '#059669', bgColor: '#ecfdf5' },
  FAILED: { label: 'Failed', color: '#ef4444', bgColor: '#fef2f2' },
  ROLLED_BACK: { label: 'Rolled Back', color: '#9ca3af', bgColor: '#f9fafb' },
};

export interface DataMigrationJob {
  id: string;
  name: string;
  entityType: MigrationEntityType;
  status: MigrationStatus;
  sourceFilename: string;
  fieldMapping: Record<string, string>;
  totalRows?: number;
  validRows?: number;
  invalidRows?: number;
  duplicateRows?: number;
  importedCount?: number;
  skippedCount?: number;
  errorCount?: number;
  validationErrors?: { row: number; errors: string[] }[];
  importErrors?: { rowNumber: number; message: string }[];
  dryRunResults?: any;
  defaultValues?: { createdIds?: string[] };
  createdBy?: { id: string; firstName: string; lastName: string };
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

// ── Batch 30 Types ──────────────────────────────────────

export type DocTemplateType =
  | 'LOAN_OFFER'
  | 'DEMAND_LETTER'
  | 'DISCHARGE_LETTER'
  | 'VARIATION_NOTICE'
  | 'SETTLEMENT_LETTER'
  | 'GENERAL_CORRESPONDENCE'
  | 'BOARD_REPORT'
  | 'COMPLIANCE_NOTICE';

export interface DocTemplateItem {
  id: string;
  name: string;
  type: DocTemplateType;
  category?: string;
  description?: string;
  content: string;
  version: number;
  isActive: boolean;
  isDefault: boolean;
  createdById: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  meta?: any;
  createdAt: string;
  updatedAt: string;
}

export interface DocTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  content: string;
  changeNote?: string;
  createdAt: string;
}

export interface GeneratedDocument {
  id: string;
  templateId: string;
  loanId: string;
  content: string;
  format: string;
  generatedAt: string;
}

export interface MergeFieldPlaceholder {
  entity: string;
  fields: { key: string; label: string; example: string }[];
}

export interface SavedReportConfig {
  id: string;
  name: string;
  description?: string;
  entityType: string;
  config: {
    filters?: { field: string; operator: string; value: any }[];
    columns?: string[];
    groupBy?: string;
    sort?: { field: string; direction: 'asc' | 'desc' };
  };
  ownerId: string;
  owner?: { id: string; firstName: string; lastName: string };
  isPublic: boolean;
  schedules?: ReportScheduleItem[];
  createdAt: string;
  updatedAt: string;
}

export interface ReportScheduleItem {
  id: string;
  reportId: string;
  cronExpr: string;
  format: string;
  recipients: string[];
  isActive: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
}

export interface ReportExecutionItem {
  id: string;
  reportId: string;
  format: string;
  rowCount: number;
  durationMs: number;
  status: string;
  error?: string;
  executedAt: string;
}

export interface ReportShareItem {
  id: string;
  reportId: string;
  userId: string;
  permission: 'VIEW' | 'EDIT';
  sharedAt: string;
}

export interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  key?: string;
  rateLimit: number;
  ipWhitelist?: string[];
  isActive: boolean;
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
  revokedAt?: string;
}

export interface ApiUsageStats {
  totalRequests: number;
  requestsToday: number;
  errorRate: number;
  avgResponseMs: number;
  byEndpoint?: { endpoint: string; count: number }[];
  byStatus?: { status: number; count: number }[];
}

export interface ApiWebhookItem {
  id: string;
  name: string;
  url: string;
  events: string[];
  secret?: string;
  isActive: boolean;
  lastCalledAt?: string;
  failCount: number;
  createdAt: string;
}

export type WorkflowTrigger =
  | 'LOAN_STATUS_CHANGE'
  | 'PAYMENT_RECEIVED'
  | 'ARREARS_THRESHOLD'
  | 'MATURITY_APPROACHING'
  | 'COVENANT_BREACH'
  | 'DOCUMENT_UPLOADED'
  | 'MANUAL';

export type WorkflowConditionType =
  | 'LOAN_STATUS_IS'
  | 'LOAN_AMOUNT_GT'
  | 'LOAN_AMOUNT_LT'
  | 'ARREARS_DAYS_GT'
  | 'LVR_GT'
  | 'LOAN_TYPE_IS'
  | 'BORROWER_TYPE_IS';

export type WorkflowActionType =
  | 'SEND_NOTIFICATION'
  | 'CREATE_DIARY_ENTRY'
  | 'CHANGE_LOAN_STATUS'
  | 'ASSIGN_TO_USER'
  | 'SEND_EMAIL'
  | 'CREATE_TASK'
  | 'ESCALATE_TO_MANAGER'
  | 'ADD_COMMENT';

export interface WorkflowRuleItem {
  id: string;
  name: string;
  description?: string;
  trigger: WorkflowTrigger;
  conditions: { type: WorkflowConditionType; value: any }[];
  actions: { type: WorkflowActionType; params: any }[];
  priority: number;
  isActive: boolean;
  createdById: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  executionCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowExecutionItem {
  id: string;
  ruleId: string;
  loanId?: string;
  triggerEvent: string;
  conditionsMet: boolean;
  actionsExecuted?: any;
  error?: string;
  executedAt: string;
}

export interface FieldAuditEntryItem {
  id: string;
  entityType: string;
  entityId: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE';
  changedById: string;
  changedBy?: { id: string; firstName: string; lastName: string };
  batchId?: string;
  isRolledBack: boolean;
  rollbackReason?: string;
  createdAt: string;
}

export interface AuditDiffView {
  entityType: string;
  entityId: string;
  fromDate: string;
  toDate: string;
  changes: { fieldName: string; oldValue?: string; newValue?: string; changedAt: string }[];
}

export interface TenantItem {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  abn?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: any;
  isActive: boolean;
  loanCount?: number;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantSettings {
  id: string;
  tenantId: string;
  defaultInterestRate?: number;
  defaultPaymentFreq?: string;
  enabledFeatures?: Record<string, boolean>;
  brandingConfig?: any;
  meta?: any;
  updatedAt: string;
}

export interface TenantUserItem {
  id: string;
  tenantId: string;
  userId: string;
  user?: { id: string; firstName: string; lastName: string; email: string; role: UserRole };
  role: string;
  isActive: boolean;
  createdAt: string;
}

export interface TenantStats {
  activeLoans: number;
  totalPrincipal: number;
  arrearsRate: number;
  loansByStatus: Record<string, number>;
  loansByType: Record<string, number>;
}

// ── Batch 31 Types ──────────────────────────────────────

export interface BorrowerPortalAccess {
  id: string;
  borrowerId: string;
  email: string;
  isActive: boolean;
  lastLoginAt?: string;
  lastLoginIp?: string;
  loginCount: number;
  borrower?: { id: string; name: string };
  createdAt: string;
}

export interface BorrowerDashboard {
  borrower: { id: string; name: string; email?: string };
  loans: {
    id: string;
    loanNumber: string;
    status: string;
    principal: number;
    outstandingBalance: number;
    nextPaymentDate?: string;
    arrearsAmount?: number;
  }[];
  totalOutstanding: number;
  totalArrears: number;
  unreadNotifications: number;
}

export type ExtensionRequestStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED';

export interface PaymentExtensionRequest {
  id: string;
  borrowerId: string;
  loanId: string;
  currentDueDate: string;
  requestedDate: string;
  reason: string;
  status: ExtensionRequestStatus;
  reviewedById?: string;
  reviewedAt?: string;
  reviewNote?: string;
  borrower?: { id: string; name: string };
  loan?: { id: string; loanNumber: string };
  createdAt: string;
}

export type SmsTemplateType =
  | 'ARREARS_REMINDER'
  | 'PAYMENT_CONFIRMATION'
  | 'SETTLEMENT_NOTICE'
  | 'MATURITY_WARNING'
  | 'GENERAL';
export type SmsDeliveryStatus = 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED' | 'UNDELIVERED';

export interface SmsTemplateItem {
  id: string;
  name: string;
  type: SmsTemplateType;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SmsLogItem {
  id: string;
  loanId?: string;
  borrowerId?: string;
  templateId?: string;
  to: string;
  message: string;
  status: SmsDeliveryStatus;
  twilioSid?: string;
  errorMessage?: string;
  costAud?: number;
  sentAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

export interface SmsStats {
  totalSent: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
  monthlyCost: number;
}

export interface SmsOptOutItem {
  id: string;
  phone: string;
  reason?: string;
  optedOutAt: string;
}

export interface PushDeviceItem {
  id: string;
  userId: string;
  deviceToken: string;
  platform: string;
  isActive: boolean;
  createdAt: string;
}

export type BreakCostMethod =
  'FLAT_FEE' | 'REMAINING_INTEREST' | 'PERCENTAGE_OF_PRINCIPAL' | 'CUSTOM';

export interface MinimumTermStatus {
  isWithinMinimumTerm: boolean;
  minimumTermMonths?: number;
  minimumTermEndDate?: string;
  daysRemaining?: number;
  minimumInterestMonths?: number;
  interestMonthsCompleted?: number;
}

export interface BreakCostCalculation {
  earlyRepaymentPenalty: number;
  minimumInterestShortfall: number;
  adminFee: number;
  dischargeFee: number;
  totalBreakCost: number;
  breakCostMethod?: BreakCostMethod;
  isWithinMinimumTerm: boolean;
}

export interface DischargeEligibility {
  canDischarge: boolean;
  breakCosts: BreakCostCalculation;
  restrictions?: string[];
  outstandingBalance: number;
  totalPayable: number;
}

export type KpiTypeEnum = 'METRIC_CARD' | 'CHART' | 'TABLE' | 'GAUGE' | 'COMPARISON';

export interface KpiDefinitionItem {
  id: string;
  name: string;
  description?: string;
  type: KpiTypeEnum;
  config: any;
  displayConfig?: { color?: string; icon?: string; format?: string; unit?: string };
  isDefault: boolean;
  isActive: boolean;
  category?: string;
  sortOrder: number;
  currentValue?: number;
  trend?: { direction: 'up' | 'down' | 'flat'; percentage: number };
  createdAt: string;
  updatedAt: string;
}

export interface KpiSnapshotItem {
  id: string;
  kpiId: string;
  value: number;
  metadata?: any;
  capturedAt: string;
}

export interface KpiDashboardLayoutItem {
  id: string;
  userId: string;
  layout: { kpiId: string; x: number; y: number; w: number; h: number }[];
  updatedAt: string;
}

export interface KpiAlertItem {
  id: string;
  kpiId: string;
  condition: 'GT' | 'LT' | 'EQ';
  threshold: number;
  isActive: boolean;
  lastTriggeredAt?: string;
  createdAt: string;
}

// ── E-Signature (DocuSeal) Types ────────────────────────

export type SigningDocumentType =
  | 'LOAN_OFFER'
  | 'DISCHARGE_LETTER'
  | 'VARIATION_NOTICE'
  | 'GUARANTEE'
  | 'SETTLEMENT_LETTER'
  | 'GENERAL';
export type SignatureStatusType =
  'PENDING' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED' | 'DECLINED';
export type SubmitterStatusType = 'PENDING' | 'VIEWED' | 'SIGNED' | 'DECLINED' | 'EXPIRED';

export interface SigningTemplateItem {
  id: string;
  name: string;
  description?: string;
  docusealTemplateId: number;
  documentType: SigningDocumentType;
  fileUrl?: string;
  fields?: any;
  isActive: boolean;
  submissionCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureSubmissionItem {
  id: string;
  docusealSubmissionId?: number;
  templateId: string;
  loanId?: string;
  status: SignatureStatusType;
  message?: string;
  expiresAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  signedDocumentUrl?: string;
  signedDocumentId?: string;
  metadata?: any;
  createdById?: string;
  template?: { id: string; name: string; documentType: string };
  loan?: { id: string; loanNumber: string };
  submitters?: SignatureSubmitterItem[];
  createdAt: string;
  updatedAt: string;
}

export interface SignatureSubmitterItem {
  id: string;
  submissionId: string;
  docusealSubmitterId?: number;
  name: string;
  email: string;
  role: string;
  status: SubmitterStatusType;
  signingUrl?: string;
  viewedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  ipAddress?: string;
  createdAt: string;
}

export interface SigningStats {
  totalSent: number;
  completed: number;
  pending: number;
  expired: number;
  declined: number;
  avgCompletionDays: number;
}

export interface SigningProgressItem {
  submission: SignatureSubmissionItem;
  signerProgress: { total: number; signed: number; pending: number; declined: number };
}

// ── Super Admin Types ──────────────────────────────────

export type SuperAdminRole = 'SUPER_ADMIN' | 'PLATFORM_ADMIN' | 'DEVELOPER' | 'SUPPORT';
export type DeploymentStatus =
  | 'PENDING'
  | 'BUILDING'
  | 'DEPLOYING'
  | 'RUNNING_QA'
  | 'QA_PASSED'
  | 'QA_FAILED'
  | 'PROMOTED'
  | 'ROLLED_BACK';
export type DevChatStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type CodeChangeStatus = 'REQUESTED' | 'IN_PROGRESS' | 'REVIEW' | 'DEPLOYED' | 'REJECTED';

export interface SuperAdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: SuperAdminRole;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export interface SuperAdminAuditLogItem {
  id: string;
  superAdminId: string;
  action: string;
  targetType?: string;
  targetId?: string;
  details?: any;
  ipAddress?: string;
  superAdmin?: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

export interface ImpersonationSessionItem {
  id: string;
  superAdminId: string;
  tenantId: string;
  userId: string;
  reason: string;
  startedAt: string;
  endedAt?: string;
  actionsPerformed?: any[];
  superAdmin?: { id: string; firstName: string; lastName: string };
  tenant?: { id: string; name: string };
  user?: { id: string; firstName: string; lastName: string; email: string };
}

export interface PlatformOverview {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  totalLoans: number;
  totalPrincipal: number;
  systemHealth: { api: string; database: string; redis: string; workers: string };
  recentAlerts: { type: string; message: string; severity: string; createdAt: string }[];
  tenantSummaries: TenantSummary[];
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  userCount: number;
  loanCount: number;
  totalPrincipal: number;
  arrearsRate: number;
  lastActivityAt?: string;
}

export interface TenantHealthDetail {
  tenant: TenantSummary;
  apiResponseMs: number;
  errorRate: number;
  activeUsers: number;
  storageUsedMb: number;
  lastDeployment?: string;
  features: Record<string, boolean>;
}

export interface PerformanceSnapshot {
  id: string;
  timestamp: string;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  apiResponseP50: number;
  apiResponseP95: number;
  apiResponseP99: number;
  activeConnections: number;
  requestsPerMinute: number;
  errorRate: number;
  queueDepth: number;
}

export interface SystemHealthStatus {
  services: {
    name: string;
    status: 'healthy' | 'degraded' | 'down';
    responseMs: number;
    lastChecked: string;
  }[];
  apiMetrics: {
    endpoint: string;
    avgMs: number;
    p95Ms: number;
    p99Ms: number;
    errorRate: number;
    requestCount: number;
  }[];
  resourceUsage: {
    cpu: number;
    memory: number;
    disk: number;
    networkIn: number;
    networkOut: number;
  };
  recentErrors: { timestamp: string; service: string; message: string; count: number }[];
}

export interface VersionCommitItem {
  id: string;
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  authorEmail?: string;
  branch: string;
  filesChanged: number;
  additions: number;
  deletions: number;
  committedAt: string;
}

export interface PlatformReleaseItem {
  id: string;
  version: string;
  title: string;
  description?: string;
  changelog?: string;
  commitSha: string;
  isPreRelease: boolean;
  releasedAt: string;
  releasedBy?: { id: string; firstName: string; lastName: string };
}

export interface DeploymentRecordItem {
  id: string;
  environment: 'staging' | 'production';
  imageTag: string;
  commitSha: string;
  status: DeploymentStatus;
  version?: string;
  deployedBy?: string;
  startedAt: string;
  completedAt?: string;
  qaResults?: { totalTests: number; passed: number; failed: number; skipped: number };
  rollbackReason?: string;
  notes?: string;
}

export interface DeploymentPipeline {
  currentStaging?: DeploymentRecordItem;
  currentProduction?: DeploymentRecordItem;
  recentDeployments: DeploymentRecordItem[];
  pendingPromotion: boolean;
}

export interface QaEnvironmentStatus {
  environment: string;
  imageTag: string;
  commitSha: string;
  deployedAt: string;
  testSummary: { total: number; passed: number; failed: number; skipped: number; duration: number };
  failingTests: { name: string; suite: string; error: string; duration: number }[];
  qaIssues: QaIssue[];
}

export interface DevChatSessionItem {
  id: string;
  title: string;
  status: DevChatStatus;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  requestedBy: string;
  assignedTo?: string;
  messages: DevChatMessageItem[];
  relatedCodeChanges?: string[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface DevChatMessageItem {
  id: string;
  sessionId: string;
  senderType: 'SUPER_ADMIN' | 'DEVELOPER' | 'AI_AGENT' | 'SYSTEM';
  senderId?: string;
  content: string;
  attachments?: { name: string; url: string; type: string }[];
  createdAt: string;
}

export interface CodeChangeRequestItem {
  id: string;
  title: string;
  description: string;
  status: CodeChangeStatus;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  requestedBy: string;
  assignedTo?: string;
  chatSessionId?: string;
  githubPrUrl?: string;
  estimatedHours?: number;
  filesAffected?: string[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface DevPortalStats {
  openBugs: number;
  openFeatures: number;
  activeChatSessions: number;
  pendingCodeChanges: number;
  recentCommits: number;
  deploymentSuccess: number;
}

// ── Feature Flag Types ─────────────────────────────────

export type PlanTier = 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE' | 'CUSTOM';

export type FeatureFlagCategory =
  'CORE' | 'COMMUNICATION' | 'DOCUMENT' | 'ANALYTICS' | 'WORKFLOW' | 'ADVANCED';

export interface FeatureDefinition {
  key: string;
  name: string;
  description: string;
  category: FeatureFlagCategory;
  defaultEnabled: boolean;
  requiredPlan: PlanTier;
  adoptionRate?: number;
  enabledCount?: number;
  totalTenants?: number;
}

export interface TenantFeatureFlagItem {
  id: string;
  tenantId: string;
  featureKey: string;
  isEnabled: boolean;
  config?: any;
  enabledAt?: string;
  updatedAt: string;
}

export interface FeatureMatrixEntry {
  tenantId: string;
  tenantName: string;
  plan: PlanTier;
  features: Record<string, boolean>;
}

// ── Announcement Types ─────────────────────────────────

export type AnnouncementType = 'INFO' | 'WARNING' | 'MAINTENANCE' | 'FEATURE' | 'URGENT';

export interface PlatformAnnouncementItem {
  id: string;
  title: string;
  body: string;
  type: AnnouncementType;
  targetType: 'ALL' | 'SPECIFIC_TENANTS' | 'PLAN_TIER';
  targetValue?: string;
  publishAt?: string;
  expiresAt?: string;
  isPublished: boolean;
  readCount: number;
  dismissCount: number;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementReadItem {
  id: string;
  announcementId: string;
  tenantId: string;
  userId?: string;
  readAt: string;
  dismissedAt?: string;
}

// ── Support Ticket Types ───────────────────────────────

export type SupportTicketCategory =
  | 'BILLING'
  | 'TECHNICAL'
  | 'FEATURE_REQUEST'
  | 'BUG_REPORT'
  | 'ACCOUNT'
  | 'DATA'
  | 'INTEGRATION'
  | 'OTHER';
export type SupportTicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type SupportTicketStatus =
  'OPEN' | 'IN_PROGRESS' | 'WAITING_ON_CUSTOMER' | 'WAITING_ON_INTERNAL' | 'RESOLVED' | 'CLOSED';

export const TICKET_PRIORITY_CONFIG: Record<
  SupportTicketPriority,
  { label: string; color: string; bgColor: string }
> = {
  LOW: { label: 'Low', color: '#6b7280', bgColor: '#f9fafb' },
  MEDIUM: { label: 'Medium', color: '#3b82f6', bgColor: '#eff6ff' },
  HIGH: { label: 'High', color: '#f59e0b', bgColor: '#fffbeb' },
  URGENT: { label: 'Urgent', color: '#ef4444', bgColor: '#fef2f2' },
};

export const TICKET_STATUS_CONFIG: Record<
  SupportTicketStatus,
  { label: string; color: string; bgColor: string }
> = {
  OPEN: { label: 'Open', color: '#ef4444', bgColor: '#fef2f2' },
  IN_PROGRESS: { label: 'In Progress', color: '#8b5cf6', bgColor: '#f5f3ff' },
  WAITING_ON_CUSTOMER: { label: 'Waiting on Customer', color: '#f59e0b', bgColor: '#fffbeb' },
  WAITING_ON_INTERNAL: { label: 'Waiting on Internal', color: '#f97316', bgColor: '#fff7ed' },
  RESOLVED: { label: 'Resolved', color: '#22c55e', bgColor: '#f0fdf4' },
  CLOSED: { label: 'Closed', color: '#6b7280', bgColor: '#f9fafb' },
};

export interface SupportTicketItem {
  id: string;
  ticketNumber: number;
  tenantId: string;
  tenantName?: string;
  createdById?: string;
  assignedToId?: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  subject: string;
  description: string;
  slaResponseBy?: string;
  slaResolveBy?: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  tags?: string[];
  replies?: SupportTicketReplyItem[];
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketReplyItem {
  id: string;
  ticketId: string;
  authorId?: string;
  authorName: string;
  authorType: 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'SYSTEM';
  content: string;
  isInternal: boolean;
  attachments?: any;
  createdAt: string;
}

export interface TicketStats {
  openTickets: number;
  inProgressTickets: number;
  avgResponseMinutes: number;
  slaComplianceRate: number;
  ticketsThisWeek: number;
  resolvedThisWeek: number;
}

// ── License & Subscription Types ───────────────────────

export interface LicenseOverview {
  mrr: number;
  arr: number;
  arpu: number;
  churnRate: number;
  mrrTrend: number;
  activeSubscriptions: number;
  totalTenants: number;
}

export interface TenantSubscriptionItem {
  tenantId: string;
  tenantName: string;
  plan: PlanTier;
  status: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
  currentUsers: number;
  maxUsers: number;
  currentLoans: number;
  maxLoans: number;
  storageUsedMb: number;
  storageQuotaMb: number;
  paymentStatus: 'CURRENT' | 'OVERDUE' | 'FAILED' | 'GRACE_PERIOD';
  renewalDate?: string;
  complianceStatus: 'COMPLIANT' | 'WARNING' | 'EXCEEDED';
  monthlyRevenue: number;
}

export interface RevenueBreakdown {
  byPlan: { plan: PlanTier; revenue: number; count: number; percentage: number }[];
  total: number;
}

export interface ChurnItem {
  tenantId: string;
  tenantName: string;
  previousPlan: PlanTier;
  churnedAt: string;
  reason?: string;
  monthlyRevenueLost: number;
}

export interface ComplianceAlert {
  tenantId: string;
  tenantName: string;
  alertType:
    'USERS_EXCEEDED' | 'LOANS_EXCEEDED' | 'STORAGE_EXCEEDED' | 'PAYMENT_OVERDUE' | 'PAYMENT_FAILED';
  severity: 'WARNING' | 'CRITICAL';
  current: number;
  limit: number;
  message: string;
}

// ── Billing Types ──────────────────────────────────────

export type SubscriptionStatusType =
  'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'PAUSED' | 'CANCELLED' | 'SUSPENDED' | 'EXPIRED';
export type BillingCycleType = 'MONTHLY' | 'YEARLY';
export type UsageMetricType =
  | 'ACTIVE_USERS'
  | 'ACTIVE_LOANS'
  | 'STORAGE_MB'
  | 'API_CALLS'
  | 'SMS_SENT'
  | 'EMAILS_SENT'
  | 'E_SIGNATURES'
  | 'DOCUMENTS_GENERATED'
  | 'WEBHOOK_CALLS';
export type InvoiceStatusType =
  'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE' | 'WRITTEN_OFF';
export type DunningStageType =
  | 'INITIAL_FAILURE'
  | 'FIRST_RETRY'
  | 'SECOND_RETRY'
  | 'FEATURE_RESTRICTED'
  | 'FINAL_NOTICE'
  | 'SUSPENDED'
  | 'RESOLVED'
  | 'OVERRIDDEN';

export const SUBSCRIPTION_STATUS_CONFIG: Record<
  SubscriptionStatusType,
  { label: string; color: string; bgColor: string }
> = {
  TRIALING: { label: 'Trial', color: '#3b82f6', bgColor: '#eff6ff' },
  ACTIVE: { label: 'Active', color: '#22c55e', bgColor: '#f0fdf4' },
  PAST_DUE: { label: 'Past Due', color: '#ef4444', bgColor: '#fef2f2' },
  PAUSED: { label: 'Paused', color: '#f59e0b', bgColor: '#fffbeb' },
  CANCELLED: { label: 'Cancelled', color: '#6b7280', bgColor: '#f9fafb' },
  SUSPENDED: { label: 'Suspended', color: '#991b1b', bgColor: '#fef2f2' },
  EXPIRED: { label: 'Expired', color: '#9ca3af', bgColor: '#f9fafb' },
};

export const PLAN_TIER_CONFIG: Record<PlanTier, { label: string; color: string; bgColor: string }> =
  {
    STARTER: { label: 'Starter', color: '#3b82f6', bgColor: '#eff6ff' },
    PROFESSIONAL: { label: 'Professional', color: '#8b5cf6', bgColor: '#f5f3ff' },
    ENTERPRISE: { label: 'Enterprise', color: '#f59e0b', bgColor: '#fffbeb' },
    CUSTOM: { label: 'Custom', color: '#059669', bgColor: '#ecfdf5' },
  };

export interface SubscriptionPlanItem {
  id: string;
  tier: PlanTier;
  name: string;
  description?: string;
  monthlyPriceAud: number;
  yearlyPriceAud: number;
  maxUsers: number;
  maxLoans: number;
  maxStorageMb: number;
  maxApiCalls: number;
  maxSmsPerMonth: number;
  enabledFeatures: string[];
  isActive: boolean;
  sortOrder: number;
}

export interface TenantSubscriptionDetail {
  id: string;
  tenantId: string;
  tenantName?: string;
  plan: SubscriptionPlanItem;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  status: SubscriptionStatusType;
  billingCycle: BillingCycleType;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  cancelAtPeriodEnd: boolean;
  discountCode?: string;
  discountPercent?: number;
  discountEndsAt?: string;
  monthlyRevenue: number;
  createdAt: string;
}

export interface UsageItem {
  metric: UsageMetricType;
  current: number;
  limit: number;
  percentage: number;
  status: 'OK' | 'WARNING' | 'EXCEEDED';
}

export interface UsageHistoryPoint {
  date: string;
  metric: UsageMetricType;
  value: number;
}

export interface BillingInvoiceItem {
  id: string;
  invoiceNumber?: string;
  tenantId: string;
  tenantName?: string;
  status: InvoiceStatusType;
  subtotalAud: number;
  gstAud: number;
  totalAud: number;
  amountPaid: number;
  amountDue: number;
  periodStart?: string;
  periodEnd?: string;
  dueDate?: string;
  paidAt?: string;
  hostedInvoiceUrl?: string;
  invoicePdfUrl?: string;
  lineItems?: { description: string; quantity: number; unitAmount: number; total: number }[];
  isWrittenOff: boolean;
  createdAt: string;
}

export interface DunningStateItem {
  id: string;
  tenantId: string;
  tenantName?: string;
  stage: DunningStageType;
  failureReason?: string;
  retryCount: number;
  nextRetryAt?: string;
  restrictedAt?: string;
  suspendedAt?: string;
  amountDue: number;
  daysSinceFailure: number;
  overriddenBy?: string;
  overrideReason?: string;
}

export interface BillingRevenueOverview {
  mrr: number;
  arr: number;
  arpu: number;
  churnRate: number;
  mrrTrend: number;
  newSubscriptions: number;
  expansionRevenue: number;
  contractionRevenue: number;
}

export interface RevenueBreakdownItem {
  plan: PlanTier;
  revenue: number;
  count: number;
  percentage: number;
}

export interface GstReportItem {
  period: string;
  revenueExGst: number;
  gstCollected: number;
  totalRevenue: number;
}

export interface BillingCreditNoteItem {
  id: string;
  tenantId: string;
  tenantName?: string;
  amount: number;
  reason: string;
  issuedBy?: string;
  createdAt: string;
}

// ── Caveats ────────────────────────────────────────────

export interface Caveat {
  id: string;
  loanId: string;
  companyName: string;
  propertyDescription: string;
  amount?: string;
  lodgedDate?: string;
  expiryDate?: string;
  state?: string;
  status: string;
  reason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}
